import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import PageHero from '../components/PageHero';
import FaqSection from '../components/FaqSection';
import CategoryGrid from '../components/CategoryGrid';
import ContactCard from '../components/ContactCard';
import CtaPanel from '../components/CtaPanel';
import NotFound from './NotFound';
import { getLocationBySlug, locationPath } from '../data/locations';
import { BUSINESS_ID } from '../data/seo';
import { company } from '../data/company';
import { quotePath } from '../utils/contactLinks';
import './LandingPage.css';

const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function AreaPage() {
  const { slug } = useParams();
  const location = getLocationBySlug(slug);
  if (!location) return <NotFound />;

  const path = locationPath(location);
  const displayName = location.name.replace(/^the /, '');
  const breadcrumbs = [
    { name: 'Delivery Areas', path: '/delivery-areas' },
    { name: displayName, path },
  ];

  const faq = [
    {
      q: `Do you deliver ceiling and drywall supplies to ${displayName}?`,
      a: `Yes. ${company.name} delivers ceiling boards, plasterboard, suspended ceiling grid, insulation and acoustic products from our George warehouse to ${location.name} — ${location.distance}. Contact us with your order and site address to arrange delivery.`,
    },
    {
      q: `How do I get a quote for a project in ${displayName}?`,
      a: `Call ${company.phones[0].number}, email ${company.email} or send your list of materials through our contact form. Include quantities and your delivery address and our sales team will reply with pricing and delivery options.`,
    },
    {
      q: 'Which brands do you supply?',
      a: 'We are master distributor for SuperTec, ETEX, Siniat and OWA, and also supply Gyproc, Knauf, Knauf Insulation, Isover, Isotherm, Aerolite, Youngman, RhinoROC, Nutec, WoodUpp, Casofour, iTe, MecoLadder and Swartland.',
    },
  ];

  const service = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Ceiling and drywall supplies delivered to ${displayName}`,
    serviceType: 'Ceiling, drywall, insulation and acoustic building supplies',
    provider: { '@id': BUSINESS_ID },
    areaServed: [displayName, ...location.nearby].map((name) => ({ '@type': 'Place', name })),
  };

  return (
    <div className="landing-page">
      <SEO
        path={path}
        title={`Ceiling & Drywall Supplies ${displayName} — From George`}
        description={`Ceiling & drywall supplies in ${displayName}: ceiling boards, plasterboard, suspended grid & insulation, delivered from our George warehouse.`}
        keywords={[
          `ceiling supplies ${displayName}`,
          `drywall supplies ${displayName}`,
          `ceiling supplier ${displayName}`,
          `drywall supplier ${displayName}`,
          `plasterboard ${displayName}`,
          `ceiling insulation ${displayName}`,
          `building supplies ${displayName}`,
        ].join(', ')}
        breadcrumbs={breadcrumbs}
        structuredData={service}
      />

      <PageHero
        breadcrumbs={breadcrumbs}
        kicker={capitalise(location.region)}
        bg="/images/bg-shopfront.jpg"
        title={<>Ceiling &amp; Drywall Supplies in {displayName}</>}
        text={`${company.name} delivers ceiling, drywall, insulation and acoustic supplies to ${location.name} — ${location.distance}.`}
        actions={
          <>
            <Link to={quotePath} className="btn btn-sky btn-large">
              Get a quote <Icon name="arrow" />
            </Link>
            <a href={company.phones[0].href} className="btn btn-ghost btn-large">
              <Icon name="phone" /> Call {company.phones[0].number}
            </a>
          </>
        }
      />

      <section className="section section-white">
        <div className="container split split-top">
          <div className="prose">
            <h2>Your Ceiling &amp; Drywall Supplier for {displayName}</h2>
            <p>{location.intro}</p>
            <p>
              As master distributor for SuperTec, ETEX, Siniat and OWA — and SABISA Accredited — we combine specialist
              product knowledge with stock you can count on. Our owner, {company.founder.name}, has more than 30 years
              of building industry experience and is an accredited Green Building Council Professional.
            </p>
          </div>
          <ContactCard title={`Ordering for ${displayName}?`} />
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <div className="section-head">
            <span className="pill">{capitalise(location.region)}</span>
            <h2 className="section-title">What {displayName} Builders Order From Us</h2>
          </div>
          <div className="grid grid-3">
            {location.focus.map((item) => (
              <div key={item.title} className="card">
                <h3 className="card-title">{item.title}</h3>
                <p className="card-text">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="landing-nearby">Also serving {location.nearby.join(', ')}.</p>
        </div>
      </section>

      <section className="section section-white">
        <div className="container">
          <div className="section-head">
            <h2 className="section-title">Products Available in {displayName}</h2>
          </div>
          <CategoryGrid />
        </div>
      </section>

      <FaqSection faq={faq} className="section-light" />

      <section className="section-tight section-white">
        <div className="container text-center">
          <Link to="/delivery-areas" className="btn btn-outline">
            All delivery areas <Icon name="arrow" size={16} />
          </Link>
        </div>
      </section>

      <CtaPanel
        title="Ordering from elsewhere?"
        text="We deliver across the Garden Route, Klein Karoo, Western Cape and Eastern Cape from our warehouse in George."
      />
    </div>
  );
}

export default AreaPage;
